import Cookies from 'universal-cookie'

import { LAST_KNOWN_LOC } from '@components/utilities/last-known-location'
import { removeUTMParameters } from '@navigation/utm-params-constants'
import { createCookieStorageWithTTL } from '@state/create-cookie-storage-with-ttl'
import { AppLocalStorage, AppSessionStorage } from '@state/storage-wrapper'
import userTraitUtils from '@utils/user-traits'

const TWO_HOURS = 1000 * 60 * 60 * 2
// Create a store with a ttl to use for location
// We want to try only keeping location data for 2 hours
const cookieTTLStore = createCookieStorageWithTTL(TWO_HOURS)

const storageKeys = {
  accessToken: 'swimply::accessToken',
  createHostPoolId: 'swimply::create_host_pool_id',
  ghostUser: 'swimply::ghost_user::',
  hostPoolCreationStartTime: 'swimply::host_pool_creation_time_start',
  hostPoolId: 'swimply::hostPoolId',
  hostPoolStatus: 'swimply::hostPoolStatus',
  incompleteHostInfo: 'swimply::host_incomplete',
  lastKnownLocation: LAST_KNOWN_LOC,
  wantsAuthentication: 'swimply::wants_authentication',
  multiDates: 'swimply::multiDates',
  poolSearchDate: 'swimply::pool_search_date',
  referralToken: 'swimply::referral_token',
  // Keeping this so we delete it when a user logs out even after switching to new auth tokens
  refreshToken: 'swimply::refreshToken',
  resetPassword: 'swimply::resetPassword',
  reviewType: 'swimply::reviewType',
  searchPoolPage: 'swimply::search_pool_page',
  subscribedLocations: 'swimply::subscribedLocations',
  userId: 'swimply::user_id',
  verificationGetStarted: 'swimply::verification_get_started',
} as const

export default {
  setGhostUser(data: boolean) {
    if (typeof window === 'undefined') return
    AppLocalStorage.setItem(storageKeys.ghostUser, `${data}`)
  },
  getGhostUser() {
    if (typeof window === 'undefined') return
    return !!AppLocalStorage.getItem(storageKeys.ghostUser)
  },

  /**
   * Deprecated - now using server session cookies
   * @returns access token
   */
  getAccessToken() {
    if (typeof window === 'undefined') return
    return AppLocalStorage.getItem(storageKeys.accessToken)
  },

  /**
   * Removes deprecated local storage access token
   */
  removeAccessToken() {
    if (typeof window === 'undefined') return
    return AppLocalStorage.removeItem(storageKeys.accessToken)
  },

  setVerificationGetStarted(status?: string) {
    if (typeof window === 'undefined') return
    AppLocalStorage.setItem(storageKeys.verificationGetStarted, status ?? '')
  },
  getVerificationGetStarted() {
    if (typeof window === 'undefined') return
    return AppLocalStorage.getItem(storageKeys.verificationGetStarted) ?? ''
  },
  setReferralToken(token?: string) {
    if (typeof window === 'undefined') return
    AppSessionStorage.setItem(storageKeys.referralToken, token ?? '')
  },
  getReferralToken() {
    if (typeof window === 'undefined') return
    return AppSessionStorage.getItem(storageKeys.referralToken) ?? ''
  },
  removeReferralToken() {
    if (typeof window === 'undefined') return
    AppSessionStorage.removeItem(storageKeys.referralToken)
  },

  setUserID(user_id: string) {
    if (typeof window === 'undefined') return
    AppLocalStorage.setItem(storageKeys.userId, user_id)
  },
  getUserID() {
    if (typeof window === 'undefined') return
    const id = AppLocalStorage.getItem(storageKeys.userId)
    return id ? Number(id) : null
  },

  setPoolSearchDate(date?: string) {
    if (typeof window === 'undefined') return
    if (date) {
      AppLocalStorage.setItem(storageKeys.poolSearchDate, date)
    } else {
      AppLocalStorage.removeItem(storageKeys.poolSearchDate)
    }
  },

  getPoolSearchDate() {
    if (typeof window === 'undefined') return
    return AppLocalStorage.getItem(storageKeys.poolSearchDate)
  },

  getCreatePoolTime() {
    if (typeof window === 'undefined') return
    return AppLocalStorage.getItem(storageKeys.hostPoolCreationStartTime)
  },
  removeCreatePoolTime() {
    if (typeof window === 'undefined') return
    AppLocalStorage.removeItem(storageKeys.hostPoolCreationStartTime)
  },

  getCreatePoolIDEvent() {
    if (typeof window === 'undefined') return
    return AppLocalStorage.getItem(storageKeys.createHostPoolId)
  },
  removeCreatePoolIDEvent() {
    if (typeof window === 'undefined') return
    AppLocalStorage.removeItem(storageKeys.createHostPoolId)
  },

  setReviewType(reviewType: string) {
    if (typeof window === 'undefined') return
    AppLocalStorage.setItem(storageKeys.reviewType, reviewType)
  },
  getReviewType() {
    if (typeof window === 'undefined') return
    return AppLocalStorage.getItem(storageKeys.reviewType)
  },

  setWantsAuthentication() {
    if (typeof window === 'undefined') return
    AppLocalStorage.setItem(storageKeys.wantsAuthentication, `${true}`)
  },
  getWantsAuthentication() {
    if (typeof window === 'undefined') return
    const wantsAuthentication = Boolean(
      AppLocalStorage.getItem(storageKeys.wantsAuthentication)
    )
    AppLocalStorage.removeItem(storageKeys.wantsAuthentication)
    return wantsAuthentication
  },
  removeWantsAuthentication() {
    if (typeof window === 'undefined') return
    AppLocalStorage.removeItem(storageKeys.wantsAuthentication)
  },

  setLastKnownLocation(location: GeolocationPosition) {
    if (typeof window === 'undefined' || !location) return
    const updatedLocation = {
      ...location,
      toJSON: undefined,
      coords: {
        ...location.coords,
        toJSON: undefined,
      },
    }

    cookieTTLStore.setItem(
      storageKeys.lastKnownLocation,
      JSON.stringify(updatedLocation)
    )
  },

  getLastKnownLocation() {
    if (typeof window === 'undefined') return

    const cookies = new Cookies()
    const position = cookies.get(storageKeys.lastKnownLocation)

    if (
      position &&
      typeof position === 'string' &&
      position !== 'undefined' &&
      position.includes('coords')
    ) {
      const parsed = JSON.parse(position)
      return parsed?.coords ? (parsed as GeolocationPosition) : undefined
    }

    const isPosition = (a?: unknown): a is GeolocationPosition => {
      if (!a) return false
      return typeof a === 'object' && Object.hasOwn(a, 'coords')
    }

    if (isPosition(position)) {
      return position
    }

    return undefined
  },

  /* Logout */
  logout() {
    if (typeof window === 'undefined') return

    userTraitUtils.removeUserContextTraits()
    userTraitUtils.removeUserLocationContextTraits()
    userTraitUtils.removeListingContextTraits()
    removeUTMParameters()

    /* Authorization */

    this.setWantsAuthentication()
    AppLocalStorage.removeItem(storageKeys.accessToken)
    AppLocalStorage.removeItem(storageKeys.refreshToken)
    AppLocalStorage.removeItem(storageKeys.userId)
    AppLocalStorage.removeItem(storageKeys.ghostUser)
    AppLocalStorage.removeItem('popupShowed')

    /* Find pool */
    AppLocalStorage.removeItem(storageKeys.resetPassword)
    AppLocalStorage.removeItem('searched_zip_codes')
    /* Host module */
    AppLocalStorage.removeItem(storageKeys.hostPoolId)
    AppLocalStorage.removeItem(storageKeys.reviewType)
    AppLocalStorage.removeItem(storageKeys.incompleteHostInfo)
    AppLocalStorage.removeItem(storageKeys.verificationGetStarted)
  },
}
